<script lang="ts">
import { computed, defineComponent, inject, ref } from "vue";
import Adresse from "@/components/person/Adresse.vue";
import ValidierungsFehler from "@/components/ValidierungsFehler.vue";
import Ueberschrift from "@/components/grid/Ueberschrift.vue";
import Zeile from "@/components/grid/Zeile.vue";
import TitelZelle from "@/components/grid/TitelZelle.vue";
import Zelle from "@/components/grid/Zelle.vue";
import Anrede from "@/components/Anrede.vue";
import { useFormStore } from "@/businesslogic/stores/formStore";

export default defineComponent({
  name: "Verwalter",
  components: {
    ValidierungsFehler,
    Adresse,
    Ueberschrift,
    Zeile,
    TitelZelle,
    Zelle,
    Anrede,
  },
  props: {
    fehler: Object,
  },
  setup() {
    const piniaStore = useFormStore();
    const istSichtbar: any = inject("istSichtbar");
    const wechsleSichtbarkeit: any = inject("wechsleSichtbarkeit");

    const isOrganisation = computed(() => {
      return (
        piniaStore.state.personendaten.verwalter.anrede === "0003" ||
        piniaStore.state.personendaten.verwalter.anrede === "WEG" ||
        piniaStore.state.personendaten.verwalter.anrede === "ETG"
      );
    });

    const isFirma = computed(() => {
      return piniaStore.state.personendaten.verwalter.anrede === "0003";
    });

    const adresseRef = ref<InstanceType<typeof Adresse>>();

    const pruefen = async () => {
      const adresseRefValide = await adresseRef.value?.pruefen(true);

      return adresseRefValide === undefined || adresseRefValide;
    };

    const eineTelefonnummerMussAngegebenWerden = computed(() => {
      return (
        !piniaStore.state.isMakler &&
        piniaStore.state.personendaten.verwalter.telefon === "" &&
        piniaStore.state.personendaten.verwalter.mobil === ""
      );
    });

    return {
      piniaStore,
      istSichtbar,
      wechsleSichtbarkeit,
      isOrganisation,
      isFirma,
      adresseRef,
      pruefen,
      eineTelefonnummerMussAngegebenWerden,
    };
  },
});
</script>

<template>
  <ueberschrift titel="Verwalter" />
  <zeile>
    <titel-zelle>Anrede und Titel</titel-zelle>
    <zelle :breite="3">
      <Anrede
        id="verwalterAnrede"
        :fehler="fehler"
        :ist-verwalter="true"
        v-model:modelValue="piniaStore.state.personendaten.verwalter.anrede"
        :required="true"
        ariaLabel="Anrede des Verwalters"
      />
    </zelle>
    <zelle :breite="3" v-if="!isOrganisation">
      <select
        id="verwalterTitel"
        name="verwalterTitel"
        v-model="piniaStore.state.personendaten.verwalter.titel"
        class="c-form-control-select"
        data-cy="verwalterTitel"
        aria-label="Titel"
      >
        <option value="">Titel</option>
        <option value="Dipl.Ing.">Dipl. Ing.</option>
        <option value="Dr.">Dr.</option>
        <option value="Prof.Dr.">Prof. Dr.</option>
      </select>
      <validierungs-fehler :fehler-text="fehler?.verwalterTitel" :reduzierter-abstand="true" />
    </zelle>
  </zeile>
  <zeile v-if="isOrganisation">
    <titel-zelle>Name der Firma</titel-zelle>
    <zelle :breite="6">
      <input
        type="text"
        id="organisation"
        name="organisation"
        v-model.lazy.trim="piniaStore.state.personendaten.verwalter.organisation"
        class="c-form-control__input"
        data-cy="vermittlerOrganisation"
        placeholder="Firma"
        minlength="2"
        maxlength="40"
        required
        aria-required="true"
        aria-label="Name der Firma"
        :data-fehlermeldung="'Bitte geben Sie den Namen der Firma ein (2-40 Zeichen).'"
      />
      <validierungs-fehler :fehler-text="fehler?.organisation" />
    </zelle>
  </zeile>
  <zeile>
    <titel-zelle
      >Vorname und Name
      <template v-if="isFirma"> (optional)</template>
    </titel-zelle>
    <zelle :breite="3">
      <input
        type="text"
        id="verwalterVorname"
        name="verwalterVorname"
        v-model.lazy.trim="piniaStore.state.personendaten.verwalter.vorname"
        class="c-form-control__input"
        data-cy="verwalterVorname"
        placeholder="Vorname"
        minlength="2"
        maxlength="40"
        :required="!isFirma"
        :aria-required="!isFirma"
        aria-label="Vorname des Verwalters"
        data-fehlermeldung="Bitte geben Sie den Vornamen ein (2-40 Zeichen)."
      />
      <validierungs-fehler :fehler-text="fehler?.verwalterVorname" />
    </zelle>
    <zelle :breite="3">
      <input
        type="text"
        id="verwalterNachname"
        name="verwalterNachname"
        v-model.lazy.trim="piniaStore.state.personendaten.verwalter.nachname"
        class="c-form-control__input"
        data-cy="verwalterNachname"
        placeholder="Name"
        minlength="2"
        maxlength="40"
        :required="!isFirma"
        :aria-required="!isFirma"
        aria-label="Nachname des Verwalters"
        data-parsley-combined-name-length="40"
        data-fehlermeldung="Bitte geben Sie den Nachnamen ein (2-40 Zeichen)."
      />
      <validierungs-fehler :fehler-text="fehler?.verwalterNachname" />
    </zelle>
  </zeile>
  <adresse
    :fehler="fehler"
    v-model:plz="piniaStore.state.personendaten.verwalter.adresse.plz"
    v-model:ort="piniaStore.state.personendaten.verwalter.adresse.ort"
    v-model:strasse="piniaStore.state.personendaten.verwalter.adresse.strasse"
    v-model:hausnummer="piniaStore.state.personendaten.verwalter.adresse.hausnummer"
    v-model:zu-pruefen="piniaStore.state.personendaten.verwalter.adresse.zuPruefen"
    :feldNamen="{
      plz: 'verwalterPlz',
      ort: 'verwalterOrt',
      strasse: 'verwalterStrasse',
      hausnummer: 'verwalterHausnummer',
    }"
    session-marker="verwalter"
    ref="adresseRef"
  ></adresse>
  <zeile>
    <titel-zelle
      >E-Mail
      {{ piniaStore.state.isMakler === true ? "(optional) " : "" }}
    </titel-zelle>
    <zelle>
      <input
        type="email"
        id="verwalterEmail"
        name="verwalterEmail"
        v-model.lazy.trim="piniaStore.state.personendaten.verwalter.email"
        class="c-form-control__input"
        data-cy="verwalterEmail"
        placeholder="E-Mail"
        minlength="2"
        maxlength="60"
        :required="!piniaStore.state.isMakler"
        :aria-required="!piniaStore.state.isMakler"
        aria-label="Email des Verwalters"
        data-fehlermeldung="Bitte geben Sie die E-Mail-Adresse ein (2-60 Zeichen)."
      />
      <validierungs-fehler :fehler-text="fehler?.verwalterEmail" />
    </zelle>
  </zeile>
  <zeile class="o-grid">
    <titel-zelle :zeige-info="!piniaStore.state.isMakler" info-box-id="verwalterTelefonInfo"
      >Telefon {{ piniaStore.state.isMakler === true ? "(optional) " : "" }}
    </titel-zelle>

    <zelle>
      <input
        type="text"
        id="verwalterTelefon"
        name="verwalterTelefon"
        v-model.lazy.trim="piniaStore.state.personendaten.verwalter.telefon"
        class="c-form-control__input"
        placeholder="Telefon"
        data-cy="verwalterTelefon"
        minlength="6"
        maxlength="30"
        :required="eineTelefonnummerMussAngegebenWerden"
        :aria-required="eineTelefonnummerMussAngegebenWerden"
        aria-label="Telefonnummer des Verwalters"
        data-checktelefonnummer="true"
      />
      <validierungs-fehler :fehler-text="fehler?.verwalterTelefon" />
      <div v-if="istSichtbar('verwalterTelefonInfo')" class="notice-box" id="verwalterTelefonInfo">
        <p>
          Bitte geben Sie die Telefonnummer an, unter der Sie tagsüber erreichbar sind. Ihre Telefonnummer wird nicht zu
          Werbezwecken verwendet, sondern lediglich für Rückfragen unserer Mitarbeiter/innen genutzt.
        </p>
      </div>
    </zelle>
  </zeile>
  <zeile>
    <titel-zelle :zeige-info="!piniaStore.state.isMakler" info-box-id="verwalterMobilInfo"> Mobiltelefon</titel-zelle>
    <zelle>
      <input
        type="text"
        id="verwalterMobile"
        name="verwalterMobile"
        v-model.lazy.trim="piniaStore.state.personendaten.verwalter.mobil"
        class="c-form-control__input"
        data-cy="verwalterMobile"
        placeholder="Mobiltelefon"
        minlength="6"
        maxlength="30"
        aria-label="Mobilfunkrufnummer des Verwalters"
        data-checkmobilnummer="true"
      />
      <validierungs-fehler :fehler-text="fehler?.verwalterMobile" />
      <div v-if="istSichtbar('verwalterMobilInfo')" class="notice-box" id="verwalterMobilInfo">
        <p>
          Ihre Mobiltelefonnummer wird nicht zu Werbezwecken verwendet, sondern lediglich für Rückfragen unserer
          Mitarbeiter/innen genutzt.
        </p>
      </div>
    </zelle>
  </zeile>
</template>

<style scoped></style>
